/**
 * 協力会社からの報告（工程管理の4回目、#103、docs/39 §6.3.3・§6.3.4、Figma R1）
 *   ReportsPanel  … 全体の工程表の上の「協力会社からの報告」（確かめ待ちがあるときだけ。3件まで・「ほか n件」・たたむ（覚える。件数が増えたら開く）・反映・見送りの記録）
 *   ReportsNotice … 案件の工程表の知らせの行（遅れの知らせの下）「協力会社からの報告 n件　山本解体「終わった」…」＋「見る」（この案件の分だけの吹き出し）
 *   ReportRow     … 1行：しるし・業者名と種類・現場名・工程名・こうなります（写真なら小さい絵とひとこと）・時刻・「見送る」「反映する」
 *   PhotoViewer   … 写真を大きく（前後に送れる。だれがいつ送ったか）
 *   useReportCount… 確かめ待ちの件数（メニューの朱の丸・ホームのカード）。メールでは知らせない
 * 反映の計算は API（contract の applyReport）。画面は「こうなります」の文を API から受け取って出すだけ。元に戻すボタンは付けない（決めごと7）
 */
import {
  mdHmJst,
  reportKindText,
  reportNoticeText,
  type ScheduleReport,
  type ScheduleReportApplied,
  type ScheduleReportCount,
  scheduleHref,
} from "@genbaryoku/contract";
import { PARTNER_LINK_MSG, stopIcon } from "./msg.js";
import { t } from "../../labels.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { CrIcon } from "../../components/Create.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { useEsc } from "./Dialogs.jsx";
import { Pop } from "./Overlap.jsx";

/** 確かめ待ちの件数（メニュー・ホーム）。2分ごと・画面に戻ったときに読み直す */
export function useReportCount(enabled: boolean): number {
  return useReportCountState(enabled).count;
}
/** 件数と、まだ届いていないか（ホームは報告のカードとほかのカードを一緒に描くため待つ。#179） */
export function useReportCountState(enabled: boolean): { count: number; loading: boolean } {
  const q = useQuery({
    queryKey: ["schedule-reports-count"],
    queryFn: () => api<ScheduleReportCount>("GET", "/schedule/reports/count"),
    enabled,
    retry: false,
    refetchInterval: enabled ? 120_000 : false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  return { count: enabled ? (q.data?.pending ?? 0) : 0, loading: enabled && q.isLoading };
}

/** 反映・見送りのあとに読み直すもの */
export function useReportRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["schedule-reports"] });
    void qc.invalidateQueries({ queryKey: ["schedule-reports-count"] });
    void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
  };
}

/** 報告の種類のアイコン（中止は製品ごと：傘／一時停止。#142 段5） */
const kindIcon = (k: "done" | "stop" | "delay" | "photo") => (k === "stop" ? stopIcon() : ({ done: "check", delay: "warn", photo: "camera" } as const)[k]);
const photoUrl = (id: string, size: "thumb" | "display") => `/api/v1/schedule/photos/${id}?size=${size}`;

/** 1行（R1 と案件の工程表の吹き出しで同じもの） */
export function ReportRow({
  r,
  showGroup = true,
  onApply,
  onDismissed,
  onPhoto,
}: {
  r: ScheduleReport;
  showGroup?: boolean;
  onApply: (r: ScheduleReport) => Promise<void>;
  onDismissed: () => void;
  onPhoto: (ids: string[], i: number, caption: string) => void;
}) {
  const [dismissAt, setDismissAt] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const caption = PARTNER_LINK_MSG.photoBy(r.partner.name, mdHmJst(r.createdAt));
  return (
    <div className={"sch-rep" + (r.stale ? " stale" : "")} data-testid="sch-report" data-report-id={r.id} data-kind={r.kind}>
      <span className={"sch-repic k-" + r.kind} aria-hidden="true">
        <CrIcon k={kindIcon(r.kind)} />
      </span>
      <span className="sch-repwho">
        <span className="l1">
          <b>{r.partner.name}</b>
          <span className={"sch-repkind" + (r.kind === "delay" ? " shu" : "")} data-testid="sch-report-kind">
            {t(reportKindText({ kind: r.kind, delayDays: r.delayDays, photoCount: r.photos.length }))}
          </span>
        </span>
        <small>{showGroup ? `${r.group.name}・${r.row?.name ?? t("（削除した{task}）")}` : (r.row?.name ?? t("（削除した{task}）"))}</small>
      </span>
      <span className="sch-repeff" data-testid="sch-report-effect">
        {r.kind === "photo" ? (
          <span className="sch-repphotos">
            {r.photos.slice(0, 3).map((p, i) => (
              <button key={p.id} type="button" className="sch-repthumb" aria-label={`写真 ${i + 1}枚目を拡大`} onClick={() => onPhoto(r.photos.map((x) => x.id), i, caption)} data-testid="sch-report-thumb">
                <img src={photoUrl(p.id, "thumb")} alt="" width={44} height={44} />
              </button>
            ))}
            {r.note ? <span className="note">「{r.note}」</span> : null}
          </span>
        ) : (
          <span className={r.stale ? "shu" : ""}>{r.effect ? t(r.effect) : null}</span>
        )}
        {r.kind !== "photo" && r.note ? <small className="note">「{r.note}」</small> : null}
      </span>
      <span className="sch-reptime num">{mdHmJst(r.createdAt)}</span>
      <span className="sch-repbtns">
        <button type="button" className="btn" disabled={busy} onClick={(e) => setDismissAt(e.currentTarget)} data-testid="sch-report-dismiss">
          {PARTNER_LINK_MSG.dismiss}
        </button>
        <button
          type="button"
          className="btn primary"
          disabled={busy || r.stale}
          title={r.stale ? PARTNER_LINK_MSG.stale : undefined}
          onClick={async () => {
            setBusy(true);
            setErr(undefined);
            try {
              await onApply(r);
            } catch (e) {
              setErr(messageOf(e));
            } finally {
              setBusy(false);
            }
          }}
          data-testid="sch-report-apply"
        >
          {PARTNER_LINK_MSG.apply}
        </button>
      </span>
      {err ? (
        <span className="sch-reperr">
          <ErrorText>{err}</ErrorText>
        </span>
      ) : null}
      {dismissAt ? (
        <DismissPop
          anchor={dismissAt}
          onClose={() => setDismissAt(null)}
          onDismiss={async (note) => {
            await api<ScheduleReport>("POST", `/schedule/reports/${r.id}/dismiss`, { note: note || null });
            setDismissAt(null);
            onDismissed();
          }}
        />
      ) : null}
    </div>
  );
}

/** 見送る：小さな吹き出し「見送るわけ（なくてもよい）」と「見送る」 */
function DismissPop({ anchor, onClose, onDismiss }: { anchor: HTMLElement; onClose: () => void; onDismiss: (note: string) => Promise<void> }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  return (
    <Pop anchor={anchor} onClose={onClose} label={PARTNER_LINK_MSG.dismiss} testId="sch-dismiss-pop">
      <label className="sch-dismisslbl" htmlFor="sch-dismiss-note">
        {PARTNER_LINK_MSG.dismissNote}
      </label>
      <input id="sch-dismiss-note" className="inp" maxLength={60} value={note} onChange={(e) => setNote(e.target.value)} data-testid="sch-dismiss-note" />
      <ErrorText>{err}</ErrorText>
      <button
        type="button"
        className="btn primary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await onDismiss(note.trim());
          } catch (e) {
            setErr(messageOf(e));
            setBusy(false);
          }
        }}
        data-testid="sch-dismiss-ok"
      >
        {PARTNER_LINK_MSG.dismiss}
      </button>
    </Pop>
  );
}

/** 写真を大きく（前後に送れる） */
export function PhotoViewer({ ids, index, caption, onClose }: { ids: string[]; index: number; caption: string | ((i: number) => string); onClose: () => void }) {
  const [i, setI] = useState(index);
  useEsc(onClose);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
      else if (e.key === "ArrowRight") setI((v) => Math.min(ids.length - 1, v + 1));
    };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [ids.length]);
  const cap = typeof caption === "function" ? caption(i) : caption;
  return createPortal(
    <div className="dim sch-photodim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sch-photoview" role="dialog" aria-modal="true" aria-label={cap} data-testid="sch-photo-viewer">
        <img src={photoUrl(ids[i]!, "display")} alt={cap} />
        <div className="bar">
          <button type="button" className="btn quiet sq" aria-label={PARTNER_LINK_MSG.prevPhoto} disabled={i <= 0} onClick={() => setI((v) => v - 1)} data-testid="sch-photo-prev">
            <CrIcon k="back" />
          </button>
          <span className="cap">
            {cap}
            <small className="num">
              {i + 1}／{ids.length}
            </small>
          </span>
          <button type="button" className="btn quiet sq" aria-label={PARTNER_LINK_MSG.nextPhoto} disabled={i >= ids.length - 1} onClick={() => setI((v) => v + 1)} data-testid="sch-photo-next">
            <CrIcon k="right" />
          </button>
          <button type="button" className="btn quiet sq" aria-label={PARTNER_LINK_MSG.close} onClick={onClose} data-testid="sch-photo-close">
            <CrIcon k="close" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 反映・見送りの記録（30日） */
function HistoryDialog({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  useEsc(onClose);
  const q = useQuery({ queryKey: ["schedule-reports", "decided"], queryFn: () => api<ScheduleReport[]>("GET", "/schedule/reports?status=decided&days=30"), staleTime: 0 });
  const [err, setErr] = useState<string | undefined>();
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg sch-wide" role="dialog" aria-modal="true" aria-label={PARTNER_LINK_MSG.history} data-testid="sch-report-history">
        <div className="dh sch-dh">
          {PARTNER_LINK_MSG.history}
          <span className="sp" />
          <button type="button" className="btn text sq" aria-label={PARTNER_LINK_MSG.close} onClick={onClose}>
            ×
          </button>
        </div>
        <div className="db">
          {q.isPending ? <p className="hint">読み込み中…</p> : null}
          {q.data && !q.data.length ? <p className="hint">{PARTNER_LINK_MSG.historyEmpty}</p> : null}
          <ErrorText>{err}</ErrorText>
          <ul className="sch-hist">
            {(q.data ?? []).map((r) => (
              <li key={r.id} data-testid="sch-history-item" data-status={r.status}>
                <span className="t">
                  <b>
                    {r.partner.name}「{reportNoticeText({ kind: r.kind, delayDays: r.delayDays, photoCount: r.photos.length })}」
                  </b>
                  <small>
                    {r.group.name}・{r.row?.name ?? t("（削除した{task}）")}
                  </small>
                  <small>
                    {r.status === "applied" ? PARTNER_LINK_MSG.decidedApplied(r.decidedBy, r.decidedAt ? mdHmJst(r.decidedAt) : "") : PARTNER_LINK_MSG.decidedDismissed(r.decidedBy, r.decidedAt ? mdHmJst(r.decidedAt) : "")}
                    {r.decidedNote ? `（${r.decidedNote}）` : ""}
                  </small>
                </span>
                {r.status === "dismissed" ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={async () => {
                      try {
                        await api("POST", `/schedule/reports/${r.id}/reopen`);
                        await q.refetch();
                        onChanged();
                      } catch (e) {
                        setErr(messageOf(e));
                      }
                    }}
                    data-testid="sch-history-reopen"
                  >
                    {PARTNER_LINK_MSG.reopen}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn" onClick={onClose}>
            {PARTNER_LINK_MSG.close}
          </button>
        </div>
      </div>
    </div>
  );
}

const FOLD_KEY = "gb.sch.reportsFold";
const readFold = (): number | null => {
  try {
    const v = window.localStorage.getItem(FOLD_KEY);
    return v == null ? null : Number(v);
  } catch {
    return null;
  }
};
const writeFold = (n: number | null) => {
  try {
    if (n == null) window.localStorage.removeItem(FOLD_KEY);
    else window.localStorage.setItem(FOLD_KEY, String(n));
  } catch {
    /* 覚えられなくても使える */
  }
};

/**
 * 全体の工程表の「協力会社からの報告」（R1）。確かめ待ちがあるときだけ出す。
 *   たたむ はその人のブラウザに「たたんだときの件数」を覚え、件数が増えたら開く
 */
/** 確かめ待ちの報告（全体の工程表の上の欄）。全体の工程表は、これが届くまで表を見えない形にする（#179：欄が後から入って表がずれる） */
export function usePendingReports() {
  return useQuery({ queryKey: ["schedule-reports", "pending"], queryFn: () => api<ScheduleReport[]>("GET", "/schedule/reports?status=pending"), staleTime: 0, refetchOnWindowFocus: true, retry: false });
}

export function ReportsPanel({ toast }: { toast: (text: string, action?: { label: string; onClick: () => void }) => void }) {
  const nav = useNavigate();
  const refresh = useReportRefresh();
  const q = usePendingReports();
  const [all, setAll] = useState(false);
  const [foldAt, setFoldAt] = useState<number | null>(() => readFold());
  const [history, setHistory] = useState(false);
  const [viewer, setViewer] = useState<{ ids: string[]; i: number; caption: string } | null>(null);
  const list = q.data ?? [];
  const n = list.length;
  const folded = foldAt != null && n <= foldAt;
  useEffect(() => {
    if (foldAt != null && n > foldAt) {
      setFoldAt(null);
      writeFold(null);
    }
  }, [n, foldAt]);
  const apply = async (r: ScheduleReport) => {
    const a = await api<ScheduleReportApplied>("POST", `/schedule/reports/${r.id}/apply`);
    refresh();
    toast(PARTNER_LINK_MSG.applied, { label: PARTNER_LINK_MSG.openSchedule, onClick: () => nav(scheduleHref({ groupId: a.groupId, scheduleId: a.scheduleId }, r.row?.id ?? null)) });
  };
  // 確かめ待ちが無いときは出さない（記録は題の段の ReportHistoryButton から）
  if (!n && !history) return null;
  const shown = all ? list : list.slice(0, 3);
  return (
    <section className="paper sch-reports" aria-label={PARTNER_LINK_MSG.reportsTitle} data-testid="sch-reports">
      <div className="sch-repshead">
        <h2>{PARTNER_LINK_MSG.reportsTitle}</h2>
        {n ? (
          <span className="chip ai" data-testid="sch-reports-count">
            {PARTNER_LINK_MSG.pendingCount(n)}
          </span>
        ) : null}
        <span className="sp" />
        <button type="button" className="btn text" onClick={() => setHistory(true)} data-testid="sch-report-history-btn">
          {PARTNER_LINK_MSG.history}
        </button>
        {n ? (
          <button
            type="button"
            className="btn text"
            aria-expanded={!folded}
            onClick={() => {
              const v = folded ? null : n;
              setFoldAt(v);
              writeFold(v);
            }}
            data-testid="sch-reports-fold"
          >
            {folded ? PARTNER_LINK_MSG.unfold : PARTNER_LINK_MSG.fold}
          </button>
        ) : null}
      </div>
      {folded ? null : (
        <div className="sch-replist">
          {shown.map((r) => (
            <ReportRow key={r.id} r={r} onApply={apply} onDismissed={() => (refresh(), toast(PARTNER_LINK_MSG.dismissed))} onPhoto={(ids, i, caption) => setViewer({ ids, i, caption })} />
          ))}
          {!all && n > 3 ? (
            <button type="button" className="btn text sch-repmore" onClick={() => setAll(true)} data-testid="sch-reports-more">
              {PARTNER_LINK_MSG.more(n - 3)}
            </button>
          ) : null}
        </div>
      )}
      {history ? <HistoryDialog onClose={() => setHistory(false)} onChanged={refresh} /> : null}
      {viewer ? <PhotoViewer ids={viewer.ids} index={viewer.i} caption={viewer.caption} onClose={() => setViewer(null)} /> : null}
    </section>
  );
}

/**
 * 案件の工程表の知らせの行（遅れの知らせの下。同じ作りの1行）。「見る」でこの案件の分だけの吹き出し。
 *   onApply は画面の側で「保存待ちの変更を先に保存 → 反映 → 読み直す」
 */
export function ReportsNotice({
  reports,
  onApply,
  onChanged,
  readOnly,
  openFor,
  onOpenDone,
}: {
  reports: ScheduleReport[];
  onApply: (r: ScheduleReport) => Promise<void>;
  onChanged: () => void;
  readOnly: boolean;
  /** 工程の「報告」を押したとき：その工程の報告で吹き出しを開く */
  openFor?: { rowId: string; anchor: HTMLElement } | null;
  onOpenDone?: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [rowId, setRowId] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ ids: string[]; i: number; caption: string } | null>(null);
  useEffect(() => {
    if (!openFor) return;
    setRowId(openFor.rowId);
    setAnchor(openFor.anchor);
    onOpenDone?.();
  }, [openFor, onOpenDone]);
  useEffect(() => {
    if (!reports.length) setAnchor(null);
  }, [reports.length]);
  if (!reports.length) return null;
  const shown = rowId ? reports.filter((r) => r.row?.id === rowId) : reports;
  const names = reports.slice(0, 3).map((r) => PARTNER_LINK_MSG.noticeItem(r.partner.name, reportNoticeText({ kind: r.kind, delayDays: r.delayDays, photoCount: r.photos.length })));
  // #171（docs/10 §5.12）：前は遅れの知らせの下の帯。いまは上の藍の帯の見出しの右のチップ（押すと報告の吹き出し）。だれの何の報告かは title
  const summary = names.join("・") + (reports.length > 3 ? `・${PARTNER_LINK_MSG.more(reports.length - 3)}` : "");
  return (
    <>
      <button
        type="button"
        className="sch-repchip"
        aria-expanded={!!anchor}
        aria-haspopup="dialog"
        title={summary}
        onClick={(e) => {
          setRowId(null);
          setAnchor(anchor ? null : e.currentTarget);
        }}
        data-testid="sch-report-notice"
      >
        <CrIcon k="clock" />
        <b data-testid="sch-report-notice-count">{PARTNER_LINK_MSG.noticeLine(reports.length)}</b>
        <span className="sr">{summary}</span>
      </button>
      {anchor ? (
        <Pop anchor={anchor} onClose={() => setAnchor(null)} label={PARTNER_LINK_MSG.reportsTitle} testId="sch-report-pop" wide>
          <div className="sch-replist in-pop">
            {(shown.length ? shown : reports).map((r) =>
              readOnly ? (
                <ReadOnlyReport key={r.id} r={r} />
              ) : (
                <ReportRow
                  key={r.id}
                  r={r}
                  showGroup={false}
                  onApply={async (x) => {
                    await onApply(x);
                  }}
                  onDismissed={onChanged}
                  onPhoto={(ids, i, caption) => setViewer({ ids, i, caption })}
                />
              ),
            )}
          </div>
        </Pop>
      ) : null}
      {viewer ? <PhotoViewer ids={viewer.ids} index={viewer.i} caption={viewer.caption} onClose={() => setViewer(null)} /> : null}
    </>
  );
}

/** 見るだけ（タブレットのスイッチが切）：反映・見送りは出さない */
function ReadOnlyReport({ r }: { r: ScheduleReport }): ReactNode {
  return (
    <div className="sch-rep ro" data-testid="sch-report">
      <span className="sch-repwho">
        <span className="l1">
          <b>{r.partner.name}</b>
          <span className="sch-repkind">{t(reportKindText({ kind: r.kind, delayDays: r.delayDays, photoCount: r.photos.length }))}</span>
        </span>
        <small>{r.row?.name ?? ""}</small>
      </span>
      <span className="sch-repeff">{r.effect ? t(r.effect) : null}</span>
    </div>
  );
}

/** 「反映・見送り記録」だけのボタン（確かめ待ちが無いときも記録を開ける。全体の工程表の題の段） */
export function ReportHistoryButton() {
  const [open, setOpen] = useState(false);
  const refresh = useReportRefresh();
  // 確かめ待ちがあるときは「協力会社からの報告」の枠の中のボタンを使う（2つ並べない）
  const pending = useReportCount(true);
  if (pending && !open) return null;
  return (
    <>
      <button type="button" className="btn text" onClick={() => setOpen(true)} data-testid="sch-report-history-open">
        {PARTNER_LINK_MSG.history}
      </button>
      {open ? <HistoryDialog onClose={() => setOpen(false)} onChanged={refresh} /> : null}
    </>
  );
}
